import React from "react";

import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

import EmptyState from "./EmptyState";
import TruncatedText from "./TruncatedText";

/**
 * A reusable, premium data table component
 *
 * @param {Array} columns - Array of column objects { header: string, accessor: string, render: function, headerClassName: string, cellClassName: string, sortable: boolean, sortKey: string }
 * @param {Array} data - Array of data objects
 * @param {string} emptyStateTitle - Title for empty state
 * @param {string} emptyStateDescription - Description for empty state
 * @param {React.ElementType} emptyStateIcon - Lucide icon component for empty state
 * @param {function} onRowClick - Optional click handler for rows
 * @param {string} className - Optional container class
 * @param {string} sortBy - Currently active sorting key
 * @param {string} direction - Sorting direction ("asc" or "desc")
 * @param {function} onSort - Callback when a sortable column header is clicked
 */
const DataTable = ({
  columns = [],
  data = [],
  emptyStateTitle = "No data found",
  emptyStateDescription = "There are no records to display at this time.",
  emptyStateIcon,
  onRowClick,
  getRowClassName,
  className = "",
  sortBy,
  direction,
  onSort,
  innerScroll = true
}) => {
  const hasMobileFilters = columns.some((col) => col.filter);

  return (
    <div
      className={`bg-white dark:bg-yale-blue-900 border border-slate-200 dark:border-yale-blue-800 rounded-xl overflow-visible shadow-sm flex flex-col ${className}`}
    >
      {/* Mobile Filters Bar */}
      {hasMobileFilters && (
        <div className="md:hidden flex flex-wrap gap-3 items-center p-3 border-b border-slate-200 dark:border-yale-blue-800 bg-yale-blue-50/20 dark:bg-yale-blue-900/30">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mr-2">Filters & Options:</span>
          <div className="flex flex-wrap gap-4 items-center">
            {columns
              .filter((col) => col.filter)
              .map((col, idx) => (
                <div key={idx} className="flex items-center gap-1.5 text-xs font-semibold text-yale-blue-600 dark:text-yale-blue-400">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">{col.header}:</span>
                  {col.filter}
                </div>
              ))}
          </div>
        </div>
      )}

      <div className={`flex-1 ${innerScroll ? "overflow-auto scrollbar-thin" : "overflow-visible"}`}>
        <table className="w-full min-w-full block md:table border-b md:border-b-0 border-slate-200 dark:border-yale-blue-800 table-auto">
          <thead className="hidden md:table-header-group md:sticky md:top-0 md:z-20 bg-yale-blue-50 dark:bg-yale-blue-800/50">
            <tr>
              {columns.map((col, idx) => {
                const isActions = col.accessor === "id" || col.isActions || (typeof col.header === "string" && col.header.toLowerCase().includes("actions"));

                const isSortable = col.sortable && onSort;
                const currentSortKey = col.sortKey || col.accessor;
                const isCurrent = sortBy === currentSortKey;

                const handleSortClick = (e) => {
                  if (!isSortable) return;

                  // Avoid triggering sort when clicking interactive elements in the header (like filter popovers)
                  if (
                    e.target.closest("button") ||
                    e.target.closest("input") ||
                    e.target.closest("select") ||
                    e.target.closest("[role='dialog']")
                  ) {
                    return;
                  }

                  let nextDirection = "asc";
                  if (isCurrent) {
                    if (direction === "asc") {
                      nextDirection = "desc";
                    } else {
                      nextDirection = null; // Cycles back to default sorting (null/clear)
                    }
                  }
                  onSort(nextDirection ? currentSortKey : null, nextDirection);
                };

                return (
                  <th
                    key={idx}
                    className={`px-6 py-3 text-left text-xs font-semibold text-yale-blue-500 dark:text-slate-400 uppercase tracking-wider ${isActions ? "w-px whitespace-nowrap md:text-center" : ""} ${col.headerClassName || ""} ${isSortable ? "cursor-pointer select-none" : ""}`}
                    onClick={isSortable ? handleSortClick : undefined}
                  >
                    {isSortable ? (
                      <div className="flex items-center gap-1.5 group">
                        <span>{col.header}</span>
                        {isCurrent ? (
                          direction === "asc" ? (
                            <ArrowUp className="w-3.5 h-3.5 text-primary shrink-0 transition-all duration-200" />
                          ) : (
                            <ArrowDown className="w-3.5 h-3.5 text-primary shrink-0 transition-all duration-200" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 opacity-40 group-hover:opacity-100 transition-opacity shrink-0" />
                        )}
                        {col.filter}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span>{col.header}</span>
                        {col.filter}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="block md:table-row-group bg-slate-50/50 dark:bg-yale-blue-900/30 md:divide-y divide-slate-200 dark:divide-yale-blue-800 p-3 md:p-0">
            {Array.isArray(data) && data.length > 0 ? (
              data.map((item, rowIdx) => (
                <tr
                  key={item.id || rowIdx}
                  className={`block md:table-row bg-white dark:bg-yale-blue-900 hover:bg-slate-50 dark:hover:bg-yale-blue-800/30 transition-colors p-4 md:p-0 border border-slate-200 dark:border-yale-blue-800 md:border-0 md:border-b rounded-xl md:rounded-none mb-4 md:mb-0 shadow-sm md:shadow-none ${onRowClick ? "cursor-pointer" : ""} ${getRowClassName ? getRowClassName(item) : ""}`}
                  onClick={() => onRowClick && onRowClick(item)}
                >
                  {columns.map((col, colIdx) => {
                    const isActions = col.accessor === "id" || col.isActions || (typeof col.header === "string" && col.header.toLowerCase().includes("actions"));
                    const shouldWrap = col.wrap || col.isPrimary;
                    return (
                      <td
                        key={colIdx}
                        className={`block md:table-cell py-2 md:px-6 md:py-4 ${isActions ? "md:whitespace-nowrap md:w-px md:text-center" : "md:whitespace-normal"} border-b border-slate-100 dark:border-yale-blue-800/50 md:border-none last:border-none ${col.isPrimary ? "bg-slate-50/50 dark:bg-yale-blue-800/20 md:bg-transparent md:dark:bg-transparent -mx-4 px-4 py-3 mb-2 rounded-t-xl md:rounded-none border-b border-slate-200 dark:border-yale-blue-800" : ""} ${col.cellClassName || ""}`}
                      >
                        {col.isPrimary ? (
                          <div className="text-left w-full md:contents">
                            <div className="md:hidden font-bold text-lg text-yale-blue-900 dark:text-slate-100 mb-1">
                              {typeof (col.render ? col.render(item[col.accessor], item) : item[col.accessor]) === "string" ? (
                                <TruncatedText
                                  text={col.render ? col.render(item[col.accessor], item) : item[col.accessor] || "N/A"}
                                  lines={3}
                                  className="text-lg font-bold"
                                />
                              ) : col.render ? (
                                col.render(item[col.accessor], item)
                              ) : (
                                item[col.accessor]
                              )}
                            </div>
                            <div className="hidden md:block">
                              {typeof (col.render ? col.render(item[col.accessor], item) : item[col.accessor]) === "string" ? (
                                <TruncatedText
                                  text={col.render ? col.render(item[col.accessor], item) : item[col.accessor] || "N/A"}
                                  lines={3}
                                  className="text-sm font-semibold text-yale-blue-900 dark:text-slate-100"
                                />
                              ) : col.render ? (
                                col.render(item[col.accessor], item)
                              ) : (
                                item[col.accessor]
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className={`flex items-center w-full md:contents justify-between`}>
                            <span className="md:hidden font-semibold text-[10px] text-yale-blue-500 dark:text-slate-400 uppercase tracking-widest shrink-0 mr-4">
                              {col.header}
                            </span>
                            <div
                              className={`flex justify-end items-center ${isActions ? "ml-auto" : "flex-1"} ${isActions ? "md:flex md:justify-center md:ml-0" : "md:block md:text-left"} text-right text-sm text-slate-700 dark:text-slate-300`}
                            >
                              {isActions || col.noTruncate ? (
                                col.render ? (
                                  col.render(item[col.accessor], item)
                                ) : (
                                  item[col.accessor] || "N/A"
                                )
                              ) : (
                                <TruncatedText
                                  text={
                                    col.render
                                      ? col.render(item[col.accessor], item)
                                      : item[col.accessor] !== undefined && item[col.accessor] !== null
                                        ? item[col.accessor]
                                        : "N/A"
                                  }
                                  lines={3}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="py-12">
                  <EmptyState icon={emptyStateIcon} title={emptyStateTitle} description={emptyStateDescription} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTable;
